// assetProgress.mjs — /asset-progress 的纯逻辑（SB-197 S3b，第二百九十二批）。
//
// 页面只做渲染，判断在这里（SB-042：可判定逻辑入 .mjs 配单测），理由具体到这一页：
//   · 「这一格谁能记」——角色矩阵与后端 role_may_record／JS ALLOWED_ROLES 是同一张表的
//     第三份抄本；抄错一格的症状是"按钮在、按下去 403"或"该有按钮的格没有按钮"，两种
//     都不会报错。这里把矩阵写成可穷举的函数，单测逐格钉住与另外两份一致。
//   · 「supersedes 该填什么」——由页面拿到的 latest 决定，不由人猜；填错的症状是 409，
//     而 409 之后要做的事（刷新再提）也在这里定死。
//   · 「巢状表怎么分层」——与台账派生视图同形（面 → 类型 → MA），一份纯函数、可判。
// 语言随界面：本文件只出 i18n 键，不落任何文案。

export const STATES = ['art_delivered', 'program_received', 'program_integrated', 'screen_verified'];
export const VALUES = ['unknown', 'not_started', 'partial', 'blocked', 'pending_review', 'done', 'not_applicable'];

// 角色矩阵（需求方 2026-09-22 裁 Q3）：美术已交＝交付方一侧（art）；程序已收／已接／画面已验＝
// 内线程序（program）；owner／admin／workspace 四态皆可；空串或未知角色一态不可。
export function recordableStates(role) {
  if (role === 'art') return ['art_delivered'];
  if (role === 'program') return ['program_received', 'program_integrated', 'screen_verified'];
  if (role === 'owner' || role === 'admin' || role === 'workspace') return [...STATES];
  return [];
}
export function mayRecord(role, state) {
  return recordableStates(role).includes(state);
}

// latest：服务端给的 MA → 态 → 最新事件。取不到一律 null（不是 undefined——页面按 null 判"尚无"）。
export function latestFor(latest, ma, state) {
  const byMa = latest && Object.prototype.hasOwnProperty.call(latest, ma) ? latest[ma] : null;
  if (!byMa || typeof byMa !== 'object') return null;
  return Object.prototype.hasOwnProperty.call(byMa, state) && byMa[state] ? byMa[state] : null;
}

// 巢状树：面（section）→ 类型（type）→ 行。与台账派生视图同形；排序确定（面按文本、类型按文本、
// MA 按编号），同一份输入永远同一棵树——探针与单测都靠这一点。
export function buildTree(rows, latest) {
  const bySection = new Map();
  for (const r of rows || []) {
    const sec = String(r.section || '—');
    const type = String(r.type || '—');
    if (!bySection.has(sec)) bySection.set(sec, new Map());
    const byType = bySection.get(sec);
    if (!byType.has(type)) byType.set(type, []);
    byType.get(type).push({
      ma: r.ma,
      row: r,
      cells: Object.fromEntries(STATES.map((s) => [s, latestFor(latest, r.ma, s)])),
    });
  }
  const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
  return [...bySection.entries()].sort((a, b) => cmp(a[0], b[0])).map(([section, byType]) => {
    const types = [...byType.entries()].sort((a, b) => cmp(a[0], b[0])).map(([type, list]) => ({
      type,
      rows: list.sort((a, b) => cmp(a.ma, b.ma)),
    }));
    const all = types.flatMap((t) => t.rows);
    return { section, types, count: all.length, progress: progressOf(all) };
  });
}

// 进度：每一态「已完成」的行数（value === 'done'），分母＝范围内行数。不算百分比——
// 分母未建立时（零行）显示 0／0，不显示 NaN%。
export function progressOf(entries) {
  const out = {};
  for (const s of STATES) out[s] = (entries || []).filter((e) => e.cells && e.cells[s] && e.cells[s].value === 'done').length;
  out.total = (entries || []).length;
  return out;
}

// 记态请求体。supersedes 取该格的最新事件 id（尚无 ⇒ null）；证据空 ⇒ 不发（键给页面显示）。
export function submitPayload({ group, ma, state, value, evidence, assetVersion, usage, note, latest }) {
  const ev = String(evidence ?? '').trim();
  if (!ma || !STATES.includes(state)) return { ok: false, errorKey: 'assetProgress.err.rule' };
  if (!VALUES.includes(value)) return { ok: false, errorKey: 'assetProgress.err.rule' };
  if (!ev) return { ok: false, errorKey: 'assetProgress.evidenceRequired' };
  const cur = latestFor(latest, ma, state);
  return {
    ok: true,
    body: {
      group: group || 'MirrorMii', ma, state, value,
      assetVersion: String(assetVersion ?? '').trim(),
      usage: String(usage ?? '').trim(),
      evidence: ev,
      note: String(note ?? '').trim(),
      supersedes: cur ? cur.id : null,
    },
  };
}

// 值 → MUI Chip 颜色。done 绿、partial／pending_review 黄、blocked 红、其余中性。
export function valueColor(value) {
  if (value === 'done') return 'success';
  if (value === 'partial' || value === 'pending_review') return 'warning';
  if (value === 'blocked') return 'error';
  return 'default';
}

// 某 MA 的全部事件，新在前（历史抽屉）。
export function historyOf(events, ma) {
  return (events || []).filter((e) => e.ma === ma).sort((a, b) => b.id - a.id);
}

// 错误 → i18n 键。409＝别人刚记了一条（刷新再提）；403＝本岗位不能记这一态；400＝规则；404＝看不见台账。
export function errorKeyFor(e) {
  const status = e && typeof e.status === 'number' ? e.status : 0;
  const code = e && e.code ? String(e.code) : '';
  if (status === 409 || code === 'SUPERSEDES_STALE') return 'assetProgress.err.stale';
  if (status === 403) return 'assetProgress.err.forbidden';
  if (status === 400) return 'assetProgress.err.rule';
  if (status === 404) return 'assetProgress.err.notFound';
  return 'assetProgress.err.failed';
}

// 查询串里的组（?group=）；不合形状退回缺省——组 slug 只有字母数字连字符。
export function groupFromSearch(search, dflt = 'MirrorMii') {
  const m = /[?&]group=([A-Za-z0-9-]+)/.exec(String(search || ''));
  return m ? m[1] : dflt;
}

// 台账明细里的值带 md 记号（`[详](#ma-0001)`、`**粗**`、反引号）——弹窗里只要能读的字：
// 链接留文字、粗体去星号、行内码去反引号。不做第二只渲染器（那是文档面的事）。
export function plainText(s) {
  return String(s ?? '')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .trim();
}

// 用途绑定一行字：「流程 · 页面」（§3 提案层；无绑定回空串，页面显「未绑定」）。
export function usageLine(usage) {
  if (!usage || typeof usage !== 'object') return '';
  const flow = plainText(usage.flow), page = plainText(usage.page);
  return [flow, page].filter(Boolean).join(' · ');
}

// 镜像行的 detail（[键, 值] 对数组）→ 弹窗行。缺、不是数组、对形状不对的一律跳过，不抛。
export function detailPairs(row) {
  const d = row && Array.isArray(row.detail) ? row.detail : [];
  return d.filter((p) => Array.isArray(p) && p.length === 2).map(([k, v]) => ({ k: plainText(k), v: plainText(v) }));
}
